// src/components/dashboard/Workshop4Dashboard.js
// Professional Workshop 4 Dashboard using Tremor components (Light Theme)
import React from 'react';
import {
    Card,
    Title,
    Text,
    Metric,
    Grid,
    DonutChart,
    BarList,
    Flex,
    Legend,
    Badge,
    BadgeDelta,
    ProgressBar,
    ProgressCircle
} from "@tremor/react";
import { useTranslation } from 'react-i18next';
import {
    Zap,
    Shield,
    AlertTriangle,
    Target,
    TrendingUp,
    Activity
} from 'lucide-react';

const Workshop4Dashboard = ({
    attackPaths = [],
    operationalScenarios = [],
    workshop4DashboardData = null,
    isLoading = false
}) => {
    const { t } = useTranslation();

    // Extract data from workshop4DashboardData
    const overallRiskScore = workshop4DashboardData?.overallRiskScore || 0;
    const topVulnerableAssets = workshop4DashboardData?.topAtRiskAssets || [];
    const topAttackTechniques = workshop4DashboardData?.topAttackTechniques || [];

    // Calculate totals
    const totalAttackPaths = attackPaths.length;
    const totalScenarios = operationalScenarios.length;
    const totalAssets = topVulnerableAssets.length;
    const totalTechniques = topAttackTechniques.length;

    // Risk level helper
    const getRiskLevel = (score) => {
        if (score >= 9) return { label: t('dashboard.workshop4.critical'), color: 'rose', deltaType: 'increase' };
        if (score >= 7) return { label: t('dashboard.workshop4.high'), color: 'orange', deltaType: 'increase' };
        if (score >= 4) return { label: t('dashboard.workshop4.medium'), color: 'amber', deltaType: 'unchanged' };
        return { label: t('dashboard.workshop4.low'), color: 'emerald', deltaType: 'decrease' };
    };

    const riskInfo = getRiskLevel(overallRiskScore);

    // Count high-risk assets (score >= 7)
    const highRiskAssets = topVulnerableAssets.filter(a => a.riskScore >= 7).length;

    // Process assets data for bar list
    const assetsChartData = topVulnerableAssets.slice(0, 5).map(asset => ({
        name: asset.name || t('common.unknown'),
        value: asset.riskScore || 0
    }));

    // Process techniques data for bar list
    const techniquesChartData = topAttackTechniques.slice(0, 5).map(tech => ({
        name: tech.name || tech.technique || t('common.unknown'),
        value: tech.count || tech.frequency || 1
    }));

    // Process scenarios by likelihood
    const scenariosByLikelihood = operationalScenarios.reduce((acc, scenario) => {
        const likelihood = scenario.likelihood || scenario.vraisemblance || 'medium';
        const likelihoodKey = likelihood.toString().toLowerCase();
        const existing = acc.find(item => item.key === likelihoodKey);
        if (existing) {
            existing.value += 1;
        } else {
            const labels = {
                'high': t('dashboard.workshop4.high'),
                'medium': t('dashboard.workshop4.medium'),
                'low': t('dashboard.workshop4.low'),
                '3': t('dashboard.workshop4.high'),
                '2': t('dashboard.workshop4.medium'),
                '1': t('dashboard.workshop4.low')
            };
            acc.push({
                key: likelihoodKey,
                name: labels[likelihoodKey] || likelihoodKey,
                value: 1
            });
        }
        return acc;
    }, []);

    // Color arrays
    const likelihoodColors = ["rose", "amber", "emerald", "gray"];

    // Check if we have any data
    const hasData = totalAttackPaths > 0 || totalScenarios > 0 || overallRiskScore > 0;

    if (isLoading) {
        return (
            <Card className="!bg-white">
                <Flex justifyContent="center" alignItems="center" className="h-40">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-600 border-t-transparent mr-3"></div>
                    <Text>{t('dashboard.status.loading')}</Text>
                </Flex>
            </Card>
        );
    }

    if (!hasData) {
        return (
            <Card className="!bg-white">
                <Flex justifyContent="center" alignItems="center" className="h-40 flex-col">
                    <Zap className="w-12 h-12 text-gray-300 mb-3" />
                    <Text className="!text-gray-500">{t('dashboard.workshop4.noData')}</Text>
                    <Text className="text-xs !text-gray-400 mt-1">{t('dashboard.workshop4.completeWorkshop4')}</Text>
                </Flex>
            </Card>
        );
    }

    return (
        <div className="space-y-6 bg-gray-50 p-4 rounded-xl">
            {/* KPI Cards Row */}
            <Grid numItems={1} numItemsSm={2} numItemsLg={4} className="gap-4">
                {/* Overall Risk Score Card */}
                <Card decoration="top" decorationColor={riskInfo.color} className="!bg-white">
                    <Flex alignItems="start" justifyContent="between">
                        <div>
                            <Text className="!text-gray-600">{t('dashboard.workshop4.overallRiskScore')}</Text>
                            <Metric className="!text-gray-900">{overallRiskScore}/10</Metric>
                            <BadgeDelta deltaType={riskInfo.deltaType} size="xs" className="mt-1">
                                {riskInfo.label}
                            </BadgeDelta>
                        </div>
                        <ProgressCircle value={overallRiskScore * 10} size="md" color={riskInfo.color}>
                            <span className="text-xs font-medium text-gray-700">{overallRiskScore}</span>
                        </ProgressCircle>
                    </Flex>
                </Card>

                {/* Attack Paths Card */}
                <Card decoration="top" decorationColor="violet" className="!bg-white">
                    <Flex alignItems="start" justifyContent="between">
                        <div>
                            <Text className="!text-gray-600">{t('dashboard.workshop4.attackPaths')}</Text>
                            <Metric className="!text-gray-900">{totalAttackPaths}</Metric>
                            <Text className="mt-1 text-xs !text-gray-500">
                                {t('dashboard.workshop4.pathsIdentified')}
                            </Text>
                        </div>
                        <div className="p-2 bg-violet-100 rounded-lg">
                            <Target className="w-6 h-6 text-violet-600" />
                        </div>
                    </Flex>
                </Card>

                {/* Operational Scenarios Card */}
                <Card decoration="top" decorationColor="indigo" className="!bg-white">
                    <Flex alignItems="start" justifyContent="between">
                        <div>
                            <Text className="!text-gray-600">{t('dashboard.workshop4.operationalScenarios')}</Text>
                            <Metric className="!text-gray-900">{totalScenarios}</Metric>
                            <Text className="mt-1 text-xs !text-gray-500">
                                {t('dashboard.workshop4.scenariosAnalyzed')}
                            </Text>
                        </div>
                        <div className="p-2 bg-indigo-100 rounded-lg">
                            <Activity className="w-6 h-6 text-indigo-600" />
                        </div>
                    </Flex>
                </Card>

                {/* High Risk Assets Card */}
                <Card decoration="top" decorationColor="rose" className="!bg-white">
                    <Flex alignItems="start" justifyContent="between">
                        <div>
                            <Text className="!text-gray-600">{t('dashboard.workshop4.highRiskAssets')}</Text>
                            <Metric className="!text-gray-900">{highRiskAssets}</Metric>
                            {highRiskAssets > 0 && (
                                <Badge color="rose" size="xs" className="mt-1">
                                    {t('dashboard.workshop4.requiresAction')}
                                </Badge>
                            )}
                        </div>
                        <div className="p-2 bg-rose-100 rounded-lg">
                            <AlertTriangle className="w-6 h-6 text-rose-600" />
                        </div>
                    </Flex>
                </Card>
            </Grid>

            {/* Risk Score Progress */}
            <Card className="!bg-white">
                <Flex>
                    <div>
                        <Text className="!text-gray-600">{t('dashboard.workshop4.riskLevelIndicator')}</Text>
                        <Metric className="!text-gray-900">{riskInfo.label}</Metric>
                    </div>
                    <Badge color={riskInfo.color} size="lg">
                        {overallRiskScore}/10
                    </Badge>
                </Flex>
                <ProgressBar value={overallRiskScore * 10} color={riskInfo.color} className="mt-3" />
                <Flex className="mt-2" justifyContent="between">
                    <Text className="text-xs !text-gray-400">{t('dashboard.workshop4.low')}</Text>
                    <Text className="text-xs !text-gray-400">{t('dashboard.workshop4.critical')}</Text>
                </Flex>
            </Card>

            {/* Charts Grid */}
            <Grid numItems={1} numItemsLg={2} className="gap-6">
                {/* Top Vulnerable Assets - Bar List */}
                <Card className="!bg-white">
                    <Title className="!text-gray-900">{t('dashboard.workshop4.topVulnerableAssets')}</Title>
                    {assetsChartData.length > 0 ? (
                        <BarList
                            data={assetsChartData}
                            className="mt-4"
                            showAnimation={true}
                            color="rose"
                        />
                    ) : (
                        <div className="h-40 flex items-center justify-center">
                            <Text className="!text-gray-400">{t('dashboard.workshop4.noAssets')}</Text>
                        </div>
                    )}
                </Card>

                {/* Top Attack Techniques - Bar List */}
                <Card className="!bg-white">
                    <Title className="!text-gray-900">{t('dashboard.workshop4.topAttackTechniques')}</Title>
                    {techniquesChartData.length > 0 ? (
                        <BarList
                            data={techniquesChartData}
                            className="mt-4"
                            showAnimation={true}
                            color="violet"
                        />
                    ) : (
                        <div className="h-40 flex items-center justify-center">
                            <Text className="!text-gray-400">{t('dashboard.workshop4.noTechniques')}</Text>
                        </div>
                    )}
                </Card>

                {/* Scenarios by Likelihood - Donut Chart */}
                <Card className="!bg-white">
                    <Title className="!text-gray-900">{t('dashboard.workshop4.scenariosByLikelihood')}</Title>
                    {scenariosByLikelihood.length > 0 ? (
                        <>
                            <DonutChart
                                className="mt-6 h-60"
                                data={scenariosByLikelihood}
                                category="value"
                                index="name"
                                colors={likelihoodColors}
                                showAnimation={true}
                                showTooltip={true}
                            />
                            <Legend
                                categories={scenariosByLikelihood.map(d => d.name)}
                                colors={likelihoodColors}
                                className="mt-4"
                            />
                        </>
                    ) : (
                        <div className="h-60 flex items-center justify-center">
                            <Text className="!text-gray-400">{t('dashboard.workshop4.noScenarios')}</Text>
                        </div>
                    )}
                </Card>

                {/* Risk Summary Card */}
                <Card className="!bg-white">
                    <Title className="!text-gray-900">{t('dashboard.workshop4.riskSummary')}</Title>
                    <Grid numItems={1} numItemsMd={2} className="gap-4 mt-4">
                        <Card decoration="left" decorationColor="violet" className="!bg-white">
                            <Flex alignItems="center" justifyContent="start" className="space-x-2">
                                <Target className="w-5 h-5 text-violet-500" />
                                <Text className="!text-gray-700">{t('dashboard.workshop4.attackPaths')}</Text>
                            </Flex>
                            <Metric className="mt-2 !text-gray-900">{totalAttackPaths}</Metric>
                        </Card>
                        <Card decoration="left" decorationColor="indigo" className="!bg-white">
                            <Flex alignItems="center" justifyContent="start" className="space-x-2">
                                <Activity className="w-5 h-5 text-indigo-500" />
                                <Text className="!text-gray-700">{t('dashboard.workshop4.techniques')}</Text>
                            </Flex>
                            <Metric className="mt-2 !text-gray-900">{totalTechniques}</Metric>
                        </Card>
                    </Grid>
                </Card>
            </Grid>
        </div>
    );
};

export default Workshop4Dashboard;
